import { API } from "@/axios-config";
import { pinia } from '@/main';
import { useUserStore } from '@/store/user';
import ActivationEmail from "@/views/ActivationEmail.vue";
import Vue from 'vue';

import VueRouter from 'vue-router';
import ActivateAccount from '../views/ActivateAccount.vue';
import HomeView from '../views/HomeView.vue';
import PasswordReset from '../views/PasswordReset.vue';
import PasswordResetEmail from '../views/PasswordResetEmail.vue';
import PipelineList from '../views/PipelineList.vue';
import PipelineRun from '../views/PipelineRun.vue';
import Pipelines from '../views/Pipelines.vue';
import Profile from '../views/Profile.vue';
import ProfileEdit from '../views/ProfileEdit.vue';

import Registration from '../views/Registration.vue';
import SignIn from '../views/SignIn.vue';

Vue.use(VueRouter)

const routes = [
  {
    path: '/',
    name: 'Home',
    component: HomeView
  },
  {
    path: '/accounts/signin',
    name: 'SignIn',
    component: SignIn
  },
  {
    path: '/accounts/raven-callback',
    name: 'RavenCallback',
    component: () => import(/* webpackChunkName: "raven-callback" */ '../views/RavenCallback.vue')
  },
  {
    path: '/accounts/casper-consent',
    name: 'CasperConsent',
    component: () => import(/* webpackChunkName: "casper-consent" */ '../views/CasperConsent.vue')
  },
  {
    path: '/accounts/signup',
    name: 'SignUp',
    component: Registration,
  },
  {
    path: '/accounts/password/reset/email',
    name: 'PasswordResetEmail',
    component: PasswordResetEmail,
  },
  {
    path: '/accounts/password/reset/change/:token',
    name: 'PasswordReset',
    component: PasswordReset,
  },
  {
    path: '/accounts/activate/:token',
    name: 'ActivateAccount',
    component: ActivateAccount,
  },
  {
    path: '/accounts/activate',
    name: 'ActivationEmail',
    component: ActivationEmail,
  },
  {
    path: '/usr/profile',
    name: 'Profile',
    component: Profile,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/usr/profileEdit',
    name: 'ProfileEdit',
    component: ProfileEdit,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/whatsnew',
    name: 'WhatsNew',
    component: () => import(/* webpackChunkName: "jupyterlab" */ '../views/WhatsNew.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  // JupyterLab
  {
    path: '/jupyter',
    name: 'JupyterLab',
    component: () => import(/* webpackChunkName: "jupyterlab" */ '../views/JupyterLab.vue'),
    meta: {
      requiresAuth: true,
      breadCrumb: 'JupyterLab',
    },
  },
  {
    path: '/storage',
    name: 'Storage',
    component: () => import(/* webpackChunkName: "storage" */ '../views/Storage.vue'),
    meta: {
      requiresAuth: true,
      breadCrumb: 'Storage',
    },
  },
  {
    path: '/storage/arcus',
    name: 'StorageArcus',
    component: () => import(/* webpackChunkName: "storage-arcus" */ '../views/ArcusStorage.vue'),
    meta: {
      requiresAuth: true,
      breadCrumb: 'ARCUS S3',
    },
  },
  {
    path: '/admin/jupyter',
    name: 'JupyterAdmin',
    component: () => import('../views/JupyterAdmin.vue'),
    meta: {
      requiresAuth: true,
      breadCrumb: 'JupyterAdmin',
    },
  },
  {
    path: '/share/:token',
    name: 'PublicShare',
    component: () => import(/* webpackChunkName: "public-share" */ '../views/PublicShare.vue'),
    meta: { bare: true },
  },
  {
    path: '/admin/service-storage',
    name: 'ServiceStorage',
    component: () => import(/* webpackChunkName: "storage-service" */ '../views/ServiceStorage.vue'),
    meta: {
      requiresAuth: true,
      breadCrumb: 'Service storage',
    },
  },
  {
    path: '/admin/users',
    name: 'UserAdmin',
    component: () => import('../views/UserAdmin.vue'),
    meta: {
      requiresAuth: true,
      breadCrumb: 'JupyterAdmin',
    },
  },
  {
    path: '/jupyter/:name',
    name: 'JupyterLabView',
    component: () => import(/* webpackChunkName: "jupyterlab" */ '../views/JupyterLabView.vue'),
    meta: {
      requiresAuth: true,
      breadCrumb: 'JupyterLab',
    },
  },
  // Pipelines
  {
    path: '/pipelines',
    component: {
      render: h => h('router-view')
    },
    meta: {
      requiresAuth: true,
      breadCrumb: 'Pipelines',
    },
    children: [
      {
        path: '',
        name: 'Pipelines',
        component: PipelineList,
      },
      {
        path: 'new',
        name: 'PipelineNew',
        component: Pipelines,
      },
      {
        path: ':id',
        component: {
          render: h => h('router-view')
        },
        meta: {},
        children: [
          {
            path: 'run',
            name: 'PipelineRun',
            component: PipelineRun,
            meta: { breadCrumb: 'Run Pipeline' }
          },
        ]
      }

    ],
  },
  // Preview
  {
    path: '/preview/:instrumentName/:datasetName/',
    name: 'Preview',
    requiresAuth: true,
    component: () => import('@/views/Preview.vue'),
  },
  // Datasets
  {
    path: '/datasets',
    component: {
      render: h => h('router-view')
    },
    meta: {
      requiresAuth: true,
      breadCrumb: 'Datasets',
    },
    children: [{
      path: '',
      name: 'Datasets',
      component: () => import('@/views/Datasets.vue'),
      meta: {},
    }]
  },
  {
    path: '/help',
    name: 'HelpDocs',
    component: () => import('@/views/HelpDocs.vue'),
  },
  {
    path: '/error/500',
    name: 'Error500',
    component: () => import('@/views/500.vue'),
  },
  {
    path: '*',
    component: () => import('@/views/400.vue'),
  }
]

const router = new VueRouter({
  routes,
  mode: 'history'
})

// Chunk files of an older build disappear when a new build is deployed; a page that was open (or cached) during the switch then
// fails to load a route. Tell the person to reload instead of leaving a blank page (App.vue shows the banner).
router.onError((error) => {
  const message = (error && (error.message || String(error))) || "";
  if (/Loading (CSS )?chunk [^ ]+ failed|ChunkLoadError|Unexpected token '<'/.test(message)) {
    window.dispatchEvent(new CustomEvent("camceadhub:new-version"));
  }
});

// Goes to the sign-in page. `expired` shows the note "your previous sign-in had expired".
function toSignIn(next, to, expired) {
  const query = { redirect: to.name };
  if (expired) query.expired = "1";
  next({ name: 'SignIn', query });
}

router.beforeEach(
  async (to, from, next) => {
    try {
      const userStore = useUserStore(pinia);
      let hadDeadSession = false;
      if (userStore.getToken && userStore.isTokenExpired()) {
        // The 8 h access token ran out. Use the refresh token (7 days) if it is still alive; otherwise the stored sign-in is
        // dead and is dropped here, so it can never be attached to, or confuse, the next sign-in.
        if (!userStore.isRefreshTokenDead()) {
          try {
            const { data } = await API.post("/api/v1/accounts/token/refresh/", { refresh: userStore.getRefreshToken });
            userStore.setToken(data["access"]);
          } catch (error) {
            // handled below
          }
        }
        if (!userStore.getToken || userStore.isTokenExpired()) {
          userStore.clearSession();
          hadDeadSession = true;
        }
      }
      if (to.matched.some(record => record.meta.requiresAuth)) {
        if (userStore.getToken) {
          try {
            await API.post("/api/v1/accounts/token/verify/", { token: userStore.getToken });
          } catch (error) {
            // Only a real "this token is no good" answer ends the session. A network error or a 5xx (for example while the
            // backend is being redeployed) used to log everybody out; the page's own requests will show that problem.
            const status = error && error.response && error.response.status;
            if (status === 401 || status === 400) {
              userStore.logout();
              toSignIn(next, to, true);
              return;
            }
          }
          next();
        } else {
          userStore.logout();
          toSignIn(next, to, hadDeadSession);
        }
      } else {
        next();
      }
    } catch (error) {
      // Whatever went wrong, never leave the navigation hanging (a blank page): fall back to the sign-in page, which works
      // without any stored state.
      console.error(error);
      try {
        useUserStore(pinia).clearSession();
      } catch (e) {
        // ignore
      }
      if (to.name === 'SignIn') {
        next();
      } else {
        toSignIn(next, to, true);
      }
    }
  });


export default router
